import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTerminology } from '@/lib/useTerminology'
import { Pencil, Trash2 } from 'lucide-react'
import { cardTitle, formCardSm, inlineInput } from '@/lib/form-styles'
import { apiErrorText } from '@/lib/api-error'
import { erfuelltRecht } from '@symphenia/shared-types'
import { useAuthStore } from '@/lib/auth.store'
import { FeldStatus, useFeld, useGruppe, useSpeicherstelle, type Datensatz, type Speicherstelle } from '@/lib/speichern'
import {
  addParticipant,
  fetchParticipants,
  fetchPhaseSlots,
  fetchUsers,
  removeParticipant,
  updateParticipant,
  savePhaseSlots,
  searchContacts,
  updateProject,
  type PhaseSlotsAntwort,
} from '../api/projects-head.api'
import { slotAusAntwort, slotAusBasis, slotDaten } from '../lib/phasen-slot'
import { alsTag } from '../project-labels'
import type { InheritedSlot, LookupContact, LookupUser, ProjectDetail, ProjectParticipant, ProjectPhase } from '../types/project.types'
import { RollenBenutzer } from './auswahl'

/**
 * AP-35 Detailblatt 3 8.1 — **Reiter Phasen & Zuständige.**
 *
 * Drei Karten: die Phasen mit Zeitraum und Anmerkung; die Rollen
 * (Projektleitung, Kalkulation, Verkauf); die Teilnehmer aus beliebigen
 * Geschäftspartnern.
 *
 * Seit AP-36 F speichert der Reiter **feldweise ohne Knopf**: Jede Phasenzeile
 * ist eine Gruppe (Von/Bis sind nur zusammen gültig) und schreibt beim Verlassen
 * der Zeile ihren eigenen Slot — nicht mehr die ganze Liste auf einen Knopfdruck.
 */
export function ProjectPhasesTab({ projekt }: { projekt: ProjectDetail }) {
  const { user } = useAuthStore()
  const darf = (recht: string) => erfuelltRecht(recht, user?.permissions)
  const darfAendern = darf('projects.project.manage')

  const [daten, setDaten] = useState<PhaseSlotsAntwort | null>(null)
  const [benutzer, setBenutzer] = useState<LookupUser[]>([])
  const [teilnehmer, setTeilnehmer] = useState<ProjectParticipant[]>([])
  const [kundenkontakt, setKundenkontakt] = useState<LookupContact | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)

  const laden = useCallback(async () => {
    try {
      const [slots, personen] = await Promise.all([fetchPhaseSlots(projekt.id), fetchParticipants(projekt.id)])
      setDaten(slots)
      setTeilnehmer(personen.participants)
      setKundenkontakt(personen.clientContact)
    } catch (e) {
      setFehler(apiErrorText(e, 'Das hat nicht geklappt.'))
    }
  }, [projekt.id])

  useEffect(() => {
    void laden()
    void fetchUsers().then(setBenutzer).catch(() => undefined)
  }, [laden])

  const abweichend = useMemo(
    () => new Map((daten?.deviatingPositions ?? []).map((d) => [d.phaseId, d.positions])),
    [daten],
  )

  const slots = useMemo(() => new Map((daten?.slots ?? []).map((s) => [s.phaseId, s])), [daten])

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <div className={`${formCardSm} lg:col-span-2`}>
        <div className="flex items-center justify-between">
          <h3 className={cardTitle}>Phasen</h3>
          {daten?.assemblyFixed?.date && (
            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
              Montage fixiert: {alsTag(daten.assemblyFixed.date)}
              {daten.assemblyFixed.time ? ` · ${daten.assemblyFixed.time}` : ''}
            </span>
          )}
        </div>

        {fehler && <p className="mt-2 text-sm text-destructive">{fehler}</p>}

        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="py-2 pr-3">Phase</th>
                <th className="py-2 pr-3">von</th>
                <th className="py-2 pr-3">bis</th>
                <th className="py-2 pr-3">Anmerkung</th>
              </tr>
            </thead>
            <tbody>
              {(daten?.phases ?? []).map((phase) => (
                <PhasenZeile
                  key={phase.id}
                  projektId={projekt.id}
                  phase={phase}
                  slot={slots.get(phase.id) ?? null}
                  abweichend={abweichend.get(phase.id)}
                  darfAendern={darfAendern}
                  onAntwort={setDaten}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Rollen
        projekt={projekt}
        benutzer={benutzer}
        darfAendern={darfAendern}
        phasen={daten?.phases ?? []}
        slots={slots}
        onAntwort={setDaten}
      />
      <Teilnehmer
        projekt={projekt}
        teilnehmer={teilnehmer}
        kundenkontakt={kundenkontakt}
        darfAendern={darfAendern}
        onChanged={laden}
      />
    </div>
  )
}

/**
 * Die Speicherstelle eines Phasen-Slots. Sie wird von der Phasenzeile **und**
 * von der Rollen-Karte benutzt — beide schreiben denselben Satz, also teilen sie
 * sich über den Schlüssel auch dieselbe Warteschlange (AP-36 7.2).
 */
function usePhasenSlot(
  projektId: string,
  phase: ProjectPhase,
  slot: InheritedSlot | null,
  onAntwort: (a: PhaseSlotsAntwort) => void,
) {
  const eigen = slot?.effective ?? null
  const daten = useMemo(() => slotDaten(eigen), [eigen])
  // Der `PUT` schickt den ganzen Slot; die Werte dafür kommen aus der Basis der
  // Stelle, damit ein stilles Neusenden nach einem 409 nicht die Änderung eines
  // anderen überschreibt (`slotAusBasis`).
  const halter = useRef<Speicherstelle<unknown> | null>(null)
  const stelle = useSpeicherstelle<Datensatz>({
    schluessel: `phaseslot:${projektId}:${phase.id}`,
    bezeichnung: `Phase ${phase.name}`,
    daten,
    schreibe: (patch, stand) =>
      savePhaseSlots(
        projektId,
        [{ ...slotAusBasis(halter.current?.daten ?? daten, patch), phaseId: phase.id }],
        stand,
      ).then((a) => {
        onAntwort(a)
        return a as unknown as Datensatz
      }),
    // Die Antwort ist die ganze Liste — auch die des 409. Nur diese Zuordnung
    // macht daraus wieder den einen Slot, gegen den die Felder vergleichen.
    werte: (a) => slotAusAntwort(a, phase.id, false),
    stand: (a) => slotAusAntwort(a, phase.id, false).updatedAt as string | undefined,
  }) as Speicherstelle<unknown>
  halter.current = stelle
  return stelle
}

/**
 * Eine Phase: Zeitraum und Anmerkung. Die Zeile ist eine **Gruppe** — „bis" ist
 * ohne „von" nicht zu beurteilen, also prüft und speichert sie gemeinsam beim
 * Verlassen der Zeile.
 */
function PhasenZeile({
  projektId,
  phase,
  slot,
  abweichend,
  darfAendern,
  onAntwort,
}: {
  projektId: string
  phase: ProjectPhase
  slot: InheritedSlot | null
  abweichend?: number
  darfAendern: boolean
  onAntwort: (a: PhaseSlotsAntwort) => void
}) {
  const stelle = usePhasenSlot(projektId, phase, slot, onAntwort)
  const gruppe = useGruppe(stelle, {
    label: phase.name,
    pruefe: (w) => {
      const von = w.startsAt ? String(w.startsAt).slice(0, 10) : ''
      const bis = w.endsAt ? String(w.endsAt).slice(0, 10) : ''
      return von && bis && bis < von ? 'bis liegt vor von' : null
    },
  })
  const von = useFeld(stelle, 'startsAt', { art: 'datum', label: 'von', gruppe, gesperrt: !darfAendern })
  const bis = useFeld(stelle, 'endsAt', { art: 'datum', label: 'bis', gruppe, gesperrt: !darfAendern })
  const notiz = useFeld(stelle, 'note', { label: 'Anmerkung', gruppe, gesperrt: !darfAendern })

  return (
    <tr
      className="border-b border-border/60"
      onFocus={gruppe.onFocus}
      onBlur={gruppe.onBlur}
      onKeyDown={(e) => {
        if (e.key === 'Escape') gruppe.verwerfen()
      }}
    >
      <td className="py-2 pr-3">
        <span className="inline-flex items-center gap-2">
          {phase.color && <span className="h-2.5 w-2.5 rounded-full" style={{ background: phase.color }} />}
          {phase.name}
          {phase.isAssembly && <span className="rounded bg-muted px-1 text-xs text-muted-foreground">Montage</span>}
          <FeldStatus status={gruppe} />
        </span>
        {abweichend ? <p className="text-xs text-muted-foreground">{abweichend} Position(en) weichen ab</p> : null}
      </td>
      <td className="py-2 pr-3">
        <input type="date" className={inlineInput} {...von.props} />
      </td>
      <td className="py-2 pr-3">
        <input type="date" className={inlineInput} {...bis.props} />
      </td>
      {/* „fixiert" und „Uhrzeit" sind am Projekt entfallen (AP-35 Nachtrag 11, N11-6) —
          vorhandene Werte gehen beim Speichern unverändert mit. */}
      {/* Zuständige stehen im Block „Rollen" (Auftraggeber 19.9.2026) — dieselbe
          Speicherstelle, nur an einer Stelle sichtbar. */}
      <td className="py-2 pr-3">
        <input className={inlineInput} {...notiz.props} />
      </td>
    </tr>
  )
}

/**
 * Blatt 3 4. — Projektleitung, Kalkulation, Verkauf; seit Nachtrag 2 dazu je
 * Phase ihr Zuständiger (AV, Produktion, Montage …). Die Auswahl zeigt die
 * Mitarbeiter mit passender Projektrolle aus der Mitarbeiterverwaltung.
 */
function Rollen({
  projekt,
  benutzer,
  darfAendern,
  phasen,
  slots,
  onAntwort,
}: {
  projekt: ProjectDetail
  benutzer: LookupUser[]
  darfAendern: boolean
  phasen: ProjectPhase[]
  slots: Map<string, InheritedSlot>
  onAntwort: (a: PhaseSlotsAntwort) => void
}) {
  const terms = useTerminology()
  // Dieselbe Stelle wie der Reiter „Daten" — wer denselben Satz schreibt, teilt
  // sich seine Warteschlange und seinen Stand (AP-36 7.2).
  const stelle = useSpeicherstelle<Datensatz>({
    schluessel: `project:${projekt.id}`,
    bezeichnung: `${terms.project} ${projekt.projectNumber}`,
    daten: projekt as unknown as Datensatz,
    schreibe: (patch, stand) => updateProject(projekt.id, patch, stand) as unknown as Promise<Datensatz>,
  }) as Speicherstelle<unknown>
  const leitung = useFeld<string | null>(stelle, 'projectLeadUserId', { art: 'auswahl', label: terms.lead, gesperrt: !darfAendern })
  const kalkulation = useFeld<string | null>(stelle, 'calculationUserId', { art: 'auswahl', label: 'Kalkulation', gesperrt: !darfAendern })
  const verkauf = useFeld<string | null>(stelle, 'salesUserId', { art: 'auswahl', label: 'Verkauf', gesperrt: !darfAendern })

  const felder = [
    { key: 'projectLeadUserId', label: terms.lead, rolle: 'LEAD', feld: leitung },
    { key: 'calculationUserId', label: 'Kalkulation', rolle: 'CALCULATION', feld: kalkulation },
    { key: 'salesUserId', label: 'Verkauf', rolle: 'SALES', feld: verkauf },
  ]

  return (
    <div className={formCardSm}>
      <h3 className={cardTitle}>Rollen</h3>
      {/* Kompakt (Auftraggeber 19.9.2026): Beschriftung links, schmale Auswahl, einspaltig. */}
      <div className="mt-3 max-w-sm space-y-1.5">
        {felder.map((z) => (
          <label key={z.key} className="flex items-center gap-2 text-sm">
            <span className="w-28 shrink-0 truncate text-muted-foreground">{z.label}</span>
            {darfAendern ? (
              <>
                <RollenBenutzer
                  className={`${inlineInput} w-44`}
                  benutzer={benutzer}
                  rolle={z.rolle}
                  leer="—"
                  wert={z.feld.props.value}
                  onChange={(v) => z.feld.setze(v || null)}
                />
                <FeldStatus status={z.feld} />
              </>
            ) : (
              <span>{benutzer.find((b) => b.id === z.feld.props.value)?.name ?? '—'}</span>
            )}
          </label>
        ))}
        {phasen
          .filter((p) => p.isActive)
          .map((p) => (
            <PhasenZustaendig
              key={p.id}
              projektId={projekt.id}
              phase={p}
              slot={slots.get(p.id) ?? null}
              benutzer={benutzer}
              darfAendern={darfAendern}
              onAntwort={onAntwort}
            />
          ))}
      </div>
    </div>
  )
}

/** Der Zuständige einer Phase — derselbe Slot wie oben in der Phasenzeile. */
function PhasenZustaendig({
  projektId,
  phase,
  slot,
  benutzer,
  darfAendern,
  onAntwort,
}: {
  projektId: string
  phase: ProjectPhase
  slot: InheritedSlot | null
  benutzer: LookupUser[]
  darfAendern: boolean
  onAntwort: (a: PhaseSlotsAntwort) => void
}) {
  const stelle = usePhasenSlot(projektId, phase, slot, onAntwort)
  const feld = useFeld<string | null>(stelle, 'responsibleUserId', { art: 'auswahl', label: phase.name, gesperrt: !darfAendern })

  return (
    <label className="flex items-center gap-2 text-sm">
      <span className="w-28 shrink-0 truncate text-muted-foreground">{phase.name}</span>
      {darfAendern ? (
        <>
          <RollenBenutzer
            className={`${inlineInput} w-44`}
            benutzer={benutzer}
            rolle={phase.key}
            leer="—"
            wert={feld.props.value}
            onChange={(v) => feld.setze(v || null)}
          />
          <FeldStatus status={feld} />
        </>
      ) : (
        <span>{benutzer.find((b) => b.id === feld.props.value)?.name ?? '—'}</span>
      )}
    </label>
  )
}

/**
 * Blatt 3 6. — Teilnehmer aus beliebigen Partnern oder von Hand; der Kunden-Kontakt
 * zuoberst. Angezeigt wird, was man zum Anrufen braucht: Name fett, Firma,
 * Telefon und Mail — immer. „zuständig für" und Notiz stehen als Text; der Stift
 * klappt die Felder auf (Auftraggeber 18.9.2026). Seit AP-36 F ist das kein
 * Bearbeitungsmodus mehr: Aufgeklappt speichert jedes Feld für sich.
 */
function Teilnehmer({
  projekt,
  teilnehmer,
  kundenkontakt,
  darfAendern,
  onChanged,
}: {
  projekt: ProjectDetail
  teilnehmer: ProjectParticipant[]
  kundenkontakt: LookupContact | null
  darfAendern: boolean
  onChanged: () => void
}) {
  const terms = useTerminology()
  const [suche, setSuche] = useState('')
  const [treffer, setTreffer] = useState<LookupContact[]>([])
  const [rolle, setRolle] = useState('')
  const [hand, setHand] = useState({ name: '', company: '', phone: '', email: '', responsibleFor: '' })
  const [fehler, setFehler] = useState<string | null>(null)

  useEffect(() => {
    const q = suche.trim()
    if (q.length < 2) {
      setTreffer([])
      return
    }
    const id = setTimeout(() => {
      void searchContacts(q).then(setTreffer).catch(() => undefined)
    }, 300)
    return () => clearTimeout(id)
  }, [suche])

  async function tu(aktion: () => Promise<unknown>) {
    try {
      setFehler(null)
      await aktion()
      onChanged()
      return true
    } catch (e) {
      setFehler(apiErrorText(e, 'Das hat nicht geklappt.'))
      return false
    }
  }

  async function hinzu(contactPersonId: string) {
    if (await tu(() => addParticipant(projekt.id, { contactPersonId, responsibleFor: rolle.trim() || undefined }))) {
      setSuche('')
      setTreffer([])
      setRolle('')
    }
  }

  /** Ein Beteiligter ohne Geschäftspartner (Nachtrag 2) — Name Pflicht, der Rest frei. */
  async function hinzuVonHand() {
    const ok = await tu(() =>
      addParticipant(projekt.id, {
        name: hand.name.trim(),
        company: hand.company.trim() || undefined,
        phone: hand.phone.trim() || undefined,
        email: hand.email.trim() || undefined,
        responsibleFor: hand.responsibleFor.trim() || undefined,
      }),
    )
    if (ok) setHand({ name: '', company: '', phone: '', email: '', responsibleFor: '' })
  }

  return (
    <div className={formCardSm}>
      <h3 className={cardTitle}>{terms.participants}</h3>
      {fehler && <p className="mt-2 text-sm text-destructive">{fehler}</p>}

      <ul className="mt-3 divide-y divide-border/60 text-sm">
        {kundenkontakt && (
          <li className="py-2">
            <Kontaktzeile
              name={`${kundenkontakt.firstName} ${kundenkontakt.lastName ?? ''}`.trim()}
              firma={kundenkontakt.client?.name}
              telefon={kundenkontakt.phone}
              mail={kundenkontakt.email}
            />
            <span className="text-xs text-muted-foreground">Ansprechpartner des Kunden · aus {terms.data}</span>
          </li>
        )}
        {teilnehmer.map((t) => (
          <TeilnehmerZeile key={t.id} t={t} darfAendern={darfAendern} tu={tu} projectId={projekt.id} />
        ))}
        {teilnehmer.length === 0 && !kundenkontakt && <li className="py-2 text-muted-foreground">Noch keine Teilnehmer.</li>}
      </ul>

      {darfAendern && (
        <div className="mt-4 space-y-2">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Teilnehmer hinzufügen</p>
          <input className={inlineInput} placeholder="aus den Geschäftspartnern suchen — Name oder Firma …" value={suche} onChange={(e) => setSuche(e.target.value)} />
          <input className={inlineInput} placeholder="zuständig für (Bauleitung, Statik …)" value={rolle} onChange={(e) => setRolle(e.target.value)} />
          {treffer.length > 0 && (
            <ul className="max-h-40 divide-y divide-border overflow-y-auto rounded-md border border-border">
              {treffer.map((k) => (
                <li key={k.id}>
                  <button type="button" className="w-full px-2 py-1.5 text-left text-sm hover:bg-accent" onClick={() => void hinzu(k.id)}>
                    <span className="font-medium">
                      {k.firstName} {k.lastName}
                    </span>
                    {k.client ? ` · ${k.client.name}` : ''}
                  </button>
                </li>
              ))}
            </ul>
          )}
          <details className="rounded-md border border-dashed border-border p-2 text-sm">
            <summary className="cursor-pointer text-muted-foreground">oder von Hand erfassen (nicht in den Geschäftspartnern)</summary>
            <div className="mt-2 grid gap-1 sm:grid-cols-2">
              <input className={inlineInput} placeholder="Name *" value={hand.name} onChange={(e) => setHand({ ...hand, name: e.target.value })} />
              <input className={inlineInput} placeholder="Firma" value={hand.company} onChange={(e) => setHand({ ...hand, company: e.target.value })} />
              <input className={inlineInput} placeholder="Telefon" value={hand.phone} onChange={(e) => setHand({ ...hand, phone: e.target.value })} />
              <input className={inlineInput} placeholder="E-Mail" value={hand.email} onChange={(e) => setHand({ ...hand, email: e.target.value })} />
              <input
                className={`${inlineInput} sm:col-span-2`}
                placeholder="zuständig für (Bauleitung, Statik …)"
                value={hand.responsibleFor}
                onChange={(e) => setHand({ ...hand, responsibleFor: e.target.value })}
              />
            </div>
            <button
              type="button"
              disabled={!hand.name.trim()}
              className="mt-2 rounded-md bg-primary px-3 py-1 text-xs text-primary-foreground disabled:opacity-50"
              onClick={() => void hinzuVonHand()}
            >
              Anlegen
            </button>
          </details>
        </div>
      )}
    </div>
  )
}

function Kontaktzeile({ name, firma, telefon, mail }: { name: string; firma?: string | null; telefon?: string | null; mail?: string | null }) {
  return (
    <span className="block">
      <span className="font-semibold">{name || '—'}</span>
      {firma && <span className="text-muted-foreground"> · {firma}</span>}
      <span className="block text-xs">
        {telefon ? (
          <a href={`tel:${telefon.replace(/\s/g, '')}`} className="text-primary hover:underline">
            {telefon}
          </a>
        ) : (
          <span className="text-muted-foreground">kein Telefon</span>
        )}
        {' · '}
        {mail ? (
          <a href={`mailto:${mail}`} className="text-primary hover:underline">
            {mail}
          </a>
        ) : (
          <span className="text-muted-foreground">keine E-Mail</span>
        )}
      </span>
    </span>
  )
}

function TeilnehmerZeile({
  t,
  darfAendern,
  tu,
  projectId,
}: {
  t: ProjectParticipant
  darfAendern: boolean
  tu: (a: () => Promise<unknown>) => Promise<boolean>
  projectId: string
}) {
  const [offen, setOffen] = useState(false)
  const vonHand = !t.contactPerson
  const stelle = useSpeicherstelle<Datensatz>({
    schluessel: `participant:${t.id}`,
    bezeichnung: t.contactPerson ? `${t.contactPerson.firstName} ${t.contactPerson.lastName ?? ''}`.trim() : (t.name ?? 'Teilnehmer'),
    daten: t as unknown as Datensatz,
    schreibe: (patch, stand) => updateParticipant(projectId, t.id, patch, stand) as Promise<Datensatz>,
  }) as Speicherstelle<unknown>
  const zustaendig = useFeld(stelle, 'responsibleFor', { label: 'zuständig für' })
  const notiz = useFeld(stelle, 'note', { label: 'Notiz' })
  const haupt = useFeld<boolean>(stelle, 'isPrimary', { art: 'schalter', label: 'Haupt-Ansprechpartner' })
  // Die Angaben von Hand gibt es nur ohne Geschäftspartner — sonst stünden zwei
  // Namen für dieselbe Person nebeneinander (Nachtrag 2).
  const name = useFeld(stelle, 'name', { label: 'Name', pflicht: vonHand })
  const firma = useFeld(stelle, 'company', { label: 'Firma' })
  const telefon = useFeld(stelle, 'phone', { label: 'Telefon' })
  const mail = useFeld(stelle, 'email', { label: 'E-Mail' })

  return (
    <li className="py-2">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <Kontaktzeile
            name={t.contactPerson ? `${t.contactPerson.firstName} ${t.contactPerson.lastName ?? ''}`.trim() : (t.name ?? '')}
            firma={t.contactPerson?.client?.name ?? t.company}
            telefon={t.contactPerson ? t.contactPerson.phone : t.phone}
            mail={t.contactPerson ? t.contactPerson.email : t.email}
          />
          {!offen && (
            <span className="block text-xs">
              {t.responsibleFor && <span className="font-medium">zuständig für {t.responsibleFor}</span>}
              {t.isPrimary && <span className="ml-1 rounded bg-primary/10 px-1 text-[10px] text-primary">Haupt-Ansprechpartner</span>}
              {vonHand && <span className="ml-1 rounded bg-muted px-1 text-[10px] text-muted-foreground">von Hand</span>}
              {t.note && <span className="block text-muted-foreground">{t.note}</span>}
            </span>
          )}
        </div>
        {darfAendern && !offen && (
          <span className="flex shrink-0 gap-2">
            <button type="button" className="text-muted-foreground hover:text-foreground" aria-label="Teilnehmer aufklappen" onClick={() => setOffen(true)}>
              <Pencil className="h-4 w-4" />
            </button>
            <button type="button" className="text-muted-foreground hover:text-destructive" aria-label="Teilnehmer entfernen" onClick={() => void tu(() => removeParticipant(projectId, t.id))}>
              <Trash2 className="h-4 w-4" />
            </button>
          </span>
        )}
      </div>
      {offen && (
        <div className="mt-2 grid gap-1 rounded-md border border-border bg-muted/30 p-2 sm:grid-cols-2">
          {vonHand && (
            <>
              <input className={inlineInput} placeholder="Name *" {...name.props} />
              <input className={inlineInput} placeholder="Firma" {...firma.props} />
              <input className={inlineInput} placeholder="Telefon" {...telefon.props} />
              <input className={inlineInput} placeholder="E-Mail" {...mail.props} />
            </>
          )}
          <input className={inlineInput} autoFocus placeholder="zuständig für …" {...zustaendig.props} />
          <input className={inlineInput} placeholder="Notiz" {...notiz.props} />
          <label className="flex items-center gap-1 text-xs text-muted-foreground sm:col-span-2">
            <input type="checkbox" {...haupt.schalter} />
            Haupt-Ansprechpartner
          </label>
          <div className="flex items-center justify-end gap-2 sm:col-span-2">
            <FeldStatus status={zustaendig} />
            <button type="button" className="rounded-md border border-border px-2 py-1 text-xs" onClick={() => setOffen(false)}>
              Schließen
            </button>
          </div>
        </div>
      )}
    </li>
  )
}
